// Adaptive (CAT) simulation attempts; the server picks each question, so only the current one is loaded
const defaultState = () => ({
  // attempts per sim id, newest first
  history: {},
  session: null,
  result: null,
  flashcardId: null,
  // engine internals, only sent for users in debug mode
  debug: null,
  // a finished attempt's answered questions: { sessionId, items }
  review: null,
  // server time minus local time, so the timer follows the server's clock
  clockOffset: 0,
});

const getters = {
  enabled: (state, getters, rootState, rootGetters) => rootGetters['user/featureEnabled']('featureAdaptiveSim'),
  adaptiveSimIds: (state, getters, rootState, rootGetters) => {
    return rootGetters['simulationConfigs/all'].filter(config => config.adaptive).map(config => config.categoryId);
  },
  // Adaptive sims are hidden from users without the flag
  hiddenSimIds: (state, getters) => (getters.enabled ? [] : getters.adaptiveSimIds),
  // Adaptive sims this user can take
  visibleSimIds: (state, getters) => (getters.enabled ? getters.adaptiveSimIds : []),
  history: state => simId => state.history[simId],
  inProgress: state => simId => (state.history[simId] || []).find(session => session.status === 'in_progress'),
  latestCompleted: state => simId => (state.history[simId] || []).find(session => session.status === 'completed'),
  session: state => state.session,
  result: state => state.result,
  flashcardId: state => state.flashcardId,
  debug: state => state.debug,
  review: state => state.review,
  clockOffset: state => state.clockOffset,
};

const mutations = {
  setHistory(state, { simId, sessions }) {
    state.history[simId] = sessions;
  },
  setSession(state, { catSession, result, flashcard, serverNow, debug }) {
    state.session = catSession;
    state.result = result || null;
    state.debug = debug || null;
    state.flashcardId = flashcard?.id || null;
    if (serverNow) state.clockOffset = Date.parse(serverNow) - Date.now();

    const sessions = state.history[catSession.categoryId] || [];
    const others = sessions.filter(session => session.id !== catSession.id);
    state.history[catSession.categoryId] = [catSession, ...others]
      .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
  },
  setReview(state, review) {
    state.review = review;
  },
  reset(state) {
    Object.assign(state, defaultState());
  },
};

// Stores the session and puts the current question in the flashcards store so the sim components can render it
const receive = ({ commit }, data) => {
  if (data.flashcard) {
    commit('flashcards/setFlashcards', { records: [data.flashcard], categoryId: data.catSession.categoryId }, { root: true });
  }
  commit('setSession', data);
  return data;
};

const actions = {
  fetchHistory({ commit, rootGetters }, simId) {
    return rootGetters['session/ajax'].get('/adaptive_sims', { params: { categoryId: simId } }).then(response => {
      commit('setHistory', { simId, sessions: response.data.catSessions });
      return response.data.catSessions;
    });
  },
  // Starts a new attempt or resumes the one in progress
  start(context, simId) {
    return context.rootGetters['session/ajax'].post('/adaptive_sims', { categoryId: simId })
      .then(response => receive(context, response.data));
  },
  fetchSession(context, sessionId) {
    return context.rootGetters['session/ajax'].get(`/adaptive_sims/${sessionId}`)
      .then(response => receive(context, response.data));
  },
  // A 409 means the server has moved on (another tab, or time ran out), so reload where it is
  answer(context, { sessionId, ...params }) {
    return context.rootGetters['session/ajax'].post(`/adaptive_sims/${sessionId}/answer`, params)
      .then(response => receive(context, response.data))
      .catch(error => {
        if (error.response?.status === 409) return context.dispatch('fetchSession', sessionId);
        throw error;
      });
  },
  // Questions from a finished attempt with the student's answers; the flashcards go in the flashcards store
  fetchReview({ commit, rootGetters }, sessionId) {
    return rootGetters['session/ajax'].get(`/adaptive_sims/${sessionId}/review`).then(response => {
      const { catSession, items, flashcards } = response.data;
      commit('flashcards/setFlashcards', { records: flashcards, categoryId: catSession.categoryId }, { root: true });
      commit('setReview', { sessionId: catSession.id, items });
      return response.data;
    });
  },
  finish(context, sessionId) {
    return context.rootGetters['session/ajax'].post(`/adaptive_sims/${sessionId}/finish`)
      .then(response => receive(context, response.data));
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  mutations,
  actions,
};
